import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChatCircle, Lightning, Brain, ShoppingCart } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const StoreClerk = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);

  const handleBuyToken = () => {
    navigate('/tokens');
    setIsMinimized(true);
  };

  const handleVerifyFact = () => {
    navigate('/oracle-brain');
    setIsMinimized(true);
  };

  const handleTalkToSomeone = () => {
    navigate('/hub');
    setIsMinimized(true);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Minimized Avatar */}
      {isMinimized && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          onClick={() => setIsMinimized(false)}
          className="fixed bottom-6 right-6 z-50 cursor-pointer"
        >
          <motion.div
            animate={{
              scale: [0.98, 1.02, 0.98],
              y: [0, -3, 0]
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-16 h-16 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF00FF] flex items-center justify-center shadow-xl border-2 border-white/20"
          >
            <ChatCircle size={32} weight="fill" className="text-white" />
          </motion.div>
          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#00FFA3] animate-pulse"></div>
        </motion.div>
      )}

      {/* Full Store Clerk Widget */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.8 }}
            transition={{ type: "spring", duration: 0.5 }}
            className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]"
          >
            {/* Speech Bubble */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-4 p-4 glass-surface border-2 border-[#00F0FF]/50 rounded-2xl shadow-2xl relative"
            >
              {/* Close Button */}
              <button
                onClick={() => setIsMinimized(true)}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Minimize clerk"
              >
                <X size={20} className="text-gray-400 hover:text-white" />
              </button>

              {/* Clerk Message */}
              <div className="pr-8">
                <p className="text-base text-white leading-relaxed mb-4">
                  <span className="inline-block mr-2">👋</span>
                  <strong className="text-[#00F0FF]">Hey friend!</strong> Welcome back to the <strong>one-stop shop for AI</strong>. Looking for something specific, or just window shopping?
                </p>

                {/* Quick Actions */}
                <div className="space-y-2">
                  <button
                    onClick={handleBuyToken}
                    className="w-full flex items-center gap-3 p-3 glass-card rounded-lg hover:bg-white/5 transition-all hover:scale-102 border border-[#FFB800]/30 hover:border-[#FFB800]/60"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#FFB800]/20 flex items-center justify-center">
                      <ShoppingCart size={20} className="text-[#FFB800]" weight="duotone" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-bold text-white">Buy a Token</p>
                      <p className="text-xs text-gray-400">Start at $5</p>
                    </div>
                  </button>

                  <button
                    onClick={handleVerifyFact}
                    className="w-full flex items-center gap-3 p-3 glass-card rounded-lg hover:bg-white/5 transition-all hover:scale-102 border border-[#00F0FF]/30 hover:border-[#00F0FF]/60"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#00F0FF]/20 flex items-center justify-center">
                      <Lightning size={20} className="text-[#00F0FF]" weight="duotone" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-bold text-white">Verify a Fact</p>
                      <p className="text-xs text-gray-400">Truth Gate</p>
                    </div>
                  </button>

                  <button
                    onClick={handleTalkToSomeone}
                    className="w-full flex items-center gap-3 p-3 glass-card rounded-lg hover:bg-white/5 transition-all hover:scale-102 border border-[#FF00FF]/30 hover:border-[#FF00FF]/60"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#FF00FF]/20 flex items-center justify-center">
                      <Brain size={20} className="text-[#FF00FF]" weight="duotone" />
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-bold text-white">Talk to Someone</p>
                      <p className="text-xs text-gray-400">13 AI personas</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Speech bubble tail */}
              <div className="absolute -bottom-2 right-8 w-4 h-4 bg-[#0B0E14] border-r-2 border-b-2 border-[#00F0FF]/50 transform rotate-45"></div>
            </motion.div>

            {/* Avatar */}
            <motion.div
              animate={{
                scale: [0.98, 1.02, 0.98],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-16 h-16 ml-auto mr-6 rounded-full bg-gradient-to-br from-[#00F0FF] to-[#FF00FF] flex items-center justify-center shadow-xl border-2 border-white/20 relative"
            >
              <ChatCircle size={32} weight="fill" className="text-white" />
              <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#00FFA3] animate-pulse"></div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StoreClerk;
